import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import { useAuth } from '../context/AuthContext'
import SEO from '../components/SEO'
import { getFandomIcon } from '../lib/fandomIcons'
import { decodeEntities } from '../lib/decodeEntities'

// ─── Fandom metadata (grad + category only; emoji comes from shared fandomIcons) ─

const FANDOM_META = {
  'Original Fiction':  { grad: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)', category: 'Original' },
  'Harry Potter':      { grad: 'linear-gradient(135deg, #B45309 0%, #7C3AED 100%)', category: 'Books' },
  'Percy Jackson':     { grad: 'linear-gradient(135deg, #1D4ED8 0%, #0D9488 100%)', category: 'Books' },
  'The Hunger Games':  { grad: 'linear-gradient(135deg, #B45309 0%, #DC2626 100%)', category: 'Books' },
  'Twilight':          { grad: 'linear-gradient(135deg, #374151 0%, #7C3AED 100%)', category: 'Books' },
  'My Hero Academia':  { grad: 'linear-gradient(135deg, #16A34A 0%, #0D9488 100%)', category: 'Anime' },
  'Naruto':            { grad: 'linear-gradient(135deg, #EA580C 0%, #EAB308 100%)', category: 'Anime' },
  'Dragon Ball Z':     { grad: 'linear-gradient(135deg, #EA580C 0%, #EAB308 100%)', category: 'Anime' },
  'Attack on Titan':   { grad: 'linear-gradient(135deg, #374151 0%, #BE123C 100%)', category: 'Anime' },
  'Demon Slayer':      { grad: 'linear-gradient(135deg, #DC2626 0%, #7C3AED 100%)', category: 'Anime' },
  'Haikyuu':           { grad: 'linear-gradient(135deg, #EA580C 0%, #1D4ED8 100%)', category: 'Anime' },
  'Jujutsu Kaisen':    { grad: 'linear-gradient(135deg, #4B5563 0%, #7C3AED 100%)', category: 'Anime' },
  'One Piece':         { grad: 'linear-gradient(135deg, #EA580C 0%, #EAB308 100%)', category: 'Anime' },
  'Vampire Diaries':   { grad: 'linear-gradient(135deg, #BE123C 0%, #7C3AED 100%)', category: 'TV' },
  'Stranger Things':   { grad: 'linear-gradient(135deg, #1E3A5F 0%, #DC2626 100%)', category: 'TV' },
  'Euphoria':          { grad: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)', category: 'TV' },
  'Outer Banks':       { grad: 'linear-gradient(135deg, #B45309 0%, #0D9488 100%)', category: 'TV' },
  'Grey\'s Anatomy':   { grad: 'linear-gradient(135deg, #BE123C 0%, #EC4899 100%)', category: 'TV' },
  'Marvel':            { grad: 'linear-gradient(135deg, #DC2626 0%, #1D4ED8 100%)', category: 'Movies' },
  'Star Wars':         { grad: 'linear-gradient(135deg, #1E3A5F 0%, #EAB308 100%)', category: 'Movies' },
  'BTS RPF':           { grad: 'linear-gradient(135deg, #EC4899 0%, #F97316 100%)', category: 'Music' },
  'One Direction':     { grad: 'linear-gradient(135deg, #1D4ED8 0%, #EC4899 100%)', category: 'Music' },
  'Taylor Swift RPF':  { grad: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)', category: 'Music' },
}

const DEFAULT_META = { grad: 'linear-gradient(135deg, #0D9488 0%, #1D4ED8 100%)', category: 'Other' }

export function getFandomMeta(name) {
  const meta = FANDOM_META[name] ?? { ...DEFAULT_META }
  return { ...meta, emoji: getFandomIcon(name) ?? '📖' }
}

function formatNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function FandomsPage() {
  const [allStories, setAllStories] = useState([])
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const { getToken } = useAuth()

  useEffect(() => {
    async function load() {
      try {
        const token = await getToken().catch(() => null)
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const { data } = await axios.get('/api/stories?limit=500', { headers })
        setAllStories(data ?? [])
      } catch (err) {
        console.error('[FandomsPage] Failed to load stories:', err)
        setAllStories([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Build fandom directory from stories
  const fandomMap = {}
  allStories.forEach(s => {
    if (!s.fandom) return
    if (!fandomMap[s.fandom]) {
      fandomMap[s.fandom] = { stories: [], writers: new Set() }
    }
    fandomMap[s.fandom].stories.push(s)
    if (s.userId) fandomMap[s.fandom].writers.add(s.userId)
  })

  let fandoms = Object.entries(fandomMap).map(([name, data]) => {
    const meta = getFandomMeta(name)
    const sorted = [...data.stories].sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0))
    return {
      name,
      storyCount:  data.stories.length,
      writerCount: data.writers.size,
      topStory:    sorted[0] ?? null,
      totalLikes:  data.stories.reduce((s, x) => s + (x.likes ?? 0), 0),
      ...meta,
    }
  }).sort((a, b) => b.storyCount - a.storyCount)

  if (search.trim()) {
    const q = search.toLowerCase()
    fandoms = fandoms.filter(f => f.name.toLowerCase().includes(q))
  }

  // Top 14 fandoms by story count for the pill row
  const topFandoms = [...fandoms].sort((a, b) => b.storyCount - a.storyCount).slice(0, 14)

  return (
    <div className="fandoms-page">
      <SEO title="Fandoms" description="Explore fanfiction and fan communities across anime, K-pop, TV shows, movies, and more on Verse." />
      <Navbar />

      {/* ── Header ── */}
      <div className="fandoms-header">
        <div className="fandoms-header__blob fandoms-header__blob--1" />
        <div className="fandoms-header__blob fandoms-header__blob--2" />
        <div className="fandoms-header__inner">
          <p className="fandoms-header__eyebrow">
            {loading ? '…' : `${Object.keys(fandomMap).length} fandoms`}
          </p>
          <h1 className="fandoms-header__title">Find your fandom</h1>
          <p className="fandoms-header__sub">Every fandom has a story waiting to be told.</p>

          <div className="fandoms-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              placeholder="Search fandoms…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button onClick={() => setSearch('')}>×</button>}
          </div>
        </div>
      </div>

      {/* ── Popular fandom pills ── */}
      {!loading && topFandoms.length > 0 && (
        <div className="fandoms-categories">
          {topFandoms.map(f => (
            <Link
              key={f.name}
              to={`/browse?fandom=${encodeURIComponent(f.name)}`}
              className="fandoms-cat-pill"
            >
              <span>{f.emoji}</span>
              {f.name}
            </Link>
          ))}
        </div>
      )}

      {/* ── Grid ── */}
      <div className="fandoms-body">
        {loading ? (
          <div className="fandoms-loading">Loading fandoms…</div>
        ) : fandoms.length === 0 ? (
          <div className="fandoms-empty">
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔭</div>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>No fandoms found</p>
            <p style={{ color: 'var(--text-faint)', fontSize: '0.9rem' }}>
              {search ? `Nothing matched "${search}"` : 'No stories in this category yet.'}
            </p>
          </div>
        ) : (
          <div className="fandoms-grid">
            {fandoms.map(f => (
              <FandomCard key={f.name} fandom={f} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Fandom card ───────────────────────────────────────────────────────────────

function FandomCard({ fandom }) {
  return (
    <Link
      to={`/browse?fandom=${encodeURIComponent(fandom.name)}`}
      className="fandom-dir-card"
    >
      {/* Gradient top */}
      <div className="fandom-dir-card__cover" style={{ background: fandom.grad }}>
        <span className="fandom-dir-card__emoji">{fandom.emoji}</span>
        <span className="fandom-dir-card__category">{fandom.category}</span>
      </div>

      {/* Info */}
      <div className="fandom-dir-card__body">
        <h3 className="fandom-dir-card__name">{fandom.name}</h3>

        <div className="fandom-dir-card__stats">
          <span>📖 {formatNum(fandom.storyCount)} {fandom.storyCount === 1 ? 'story' : 'stories'}</span>
          {fandom.writerCount > 0 && <span>✍️ {formatNum(fandom.writerCount)} {fandom.writerCount === 1 ? 'writer' : 'writers'}</span>}
          {fandom.totalLikes > 0  && <span>❤️ {formatNum(fandom.totalLikes)}</span>}
        </div>

        {fandom.topStory && (
          <div className="fandom-dir-card__top">
            <span className="fandom-dir-card__top-label">Top story</span>
            <span className="fandom-dir-card__top-title">{decodeEntities(fandom.topStory.title)}</span>
          </div>
        )}
      </div>
    </Link>
  )
}
